import type { StackProps } from '@mui/material/Stack';
import Stack from '@mui/material/Stack';
import { useAtom } from 'jotai';
import { notificationState } from '@/recoil/atom';
import React, { useEffect, useRef, useState } from 'react';
import NotificationItem from '@/components/@IntranetLibrary/Notification/NotificationItem';
import TalkFLowModal, {
  TalkFlowModalRef,
} from '@/components/Record/TalkFLowModal';
import ApprovalModal from '@/components/Approval/ApprovalModal';
import { ModalRef } from '@/components/@IntranetLibrary/Modal';
import useModal from '@/hooks/useModal';
import { getIntranetDocUrl } from '@/utils/urls';
import useApprovalData from '@/hooks/approval/useApprovalData';
import useApproval from '@/hooks/approval/useApproval';
import useNextApprovalDoc from '@/components/Approval/@hooks/useNextApprovalDoc';
import type { NextApprovalDoc } from '@/components/Approval/@hooks/useNextApprovalDoc';
import NextApprovalConfirmModal from '@/components/Approval/NextApprovalConfirmModal';
import type { ApprovalDecisionAction } from '@/hooks/approval/useApprovalDecision';
import { NATIVE_VIEW_APPROVAL_TYPES } from '@/constant';

type NotificationListPopupProps = StackProps;

const NewNotificationsPopup = (props: NotificationListPopupProps) => {
  const [notifications, setNotifications] = useAtom(notificationState);
  const [approvalDocId, setApprovalDocId] = useState('');
  const approvalModalRef = useRef<ModalRef>(null);
  const { dataItemChange } = useApprovalData();

  // 알림 payload 엔 품의 type 이 없어, 단건조회로 type을 확보해야 ApprovalModal이 native(type 1) 분기를 탈 수 있다.
  const { data: approvalDetail, error: approvalDetailError } = useApproval(
    approvalDocId || null,
  );
  // 다음 문서로 스왑할 때 단건조회 응답 전까지 쓸 양식 코드 — 없으면 native 문서도 iframe 으로 잠깐 떴다 전환됨
  const [swappedType, setSwappedType] = useState<string>();
  // useApproval 은 keepPreviousData 미사용 → data 는 항상 현재 docId 응답.
  const approvalType = approvalDetail
    ? String(approvalDetail.type.code)
    : swappedType;
  // type(또는 조회 실패) 확정 후에 모달을 연다.
  const pendingOpenRef = useRef(false);

  const recordDetailModalRef = useRef<TalkFlowModalRef>(null);

  const openTalkFlowModal = (docId: string) => {
    recordDetailModalRef.current?.open(docId);
  };

  const { openModal: openApprovalModal, closeModal: closeApprovalModal } =
    useModal(approvalModalRef);

  const isNativeType =
    !!approvalType && NATIVE_VIEW_APPROVAL_TYPES.includes(Number(approvalType));

  // 레거시 양식은 다음 문서 선정도 레거시 몫 — 포탈이 고르면 큐가 어긋남
  const { nextDoc, promptNext, clearNextDoc } = useNextApprovalDoc(
    approvalDocId,
    !!approvalDocId && isNativeType,
  );

  /** 모달은 유지 — docId/양식 스왑으로 같은 모달에 다음 문서 로드 */
  const openDoc = (next: NextApprovalDoc) => {
    setSwappedType(
      next.typeCode === undefined ? undefined : String(next.typeCode),
    );
    setApprovalDocId(next.docId);
    clearNextDoc();
  };

  const handleDecided = async (
    decidedDocId: string,
    action: ApprovalDecisionAction,
  ) => {
    // 보류(H)는 결재 큐에 남아 본인이 재처리 — 대상 아님
    if (action === 'hold') return;
    dataItemChange?.(decidedDocId, {
      status: action === 'reject' ? 'R' : 'C',
    });
    const { next, stale } = await promptNext(decidedDocId, action);
    if (!next && !stale) closeApprovalModal();
  };

  const handleOpen = (clickedDocId: string) => {
    dataItemChange?.(clickedDocId, { read: 1 });
    setSwappedType(undefined); // 이전 흐름에서 스왑한 양식 코드가 새 문서에 붙지 않게
    clearNextDoc(); // 새로 여는 흐름 — 직전 결재의 응답이 늦게 와서 덮어쓰지 않게
    // 같은 문서 재클릭(이미 조회됨)은 state 변화가 없어 아래 effect 가 안 도므로 즉시 오픈.
    if (
      clickedDocId === approvalDocId &&
      (approvalType || approvalDetailError)
    ) {
      openApprovalModal();
      return;
    }
    pendingOpenRef.current = true;
    setApprovalDocId(clickedDocId);
  };

  useEffect(() => {
    if (pendingOpenRef.current && (approvalType || approvalDetailError)) {
      pendingOpenRef.current = false;
      openApprovalModal();
    }
  }, [approvalType, approvalDetailError, openApprovalModal]);

  // 알림 제거 함수
  const removeNotificationById = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.messageId !== id));
  };

  return (
    <>
      <Stack direction={'column'} gap={'8px'} {...props}>
        {notifications.map((item) => {
          return (
            <NotificationItem
              key={item.messageId}
              {...item}
              onClose={() => removeNotificationById(item.messageId)}
              openTalkFlowModal={openTalkFlowModal}
              handleOpen={handleOpen}
            />
          );
        })}
      </Stack>
      <TalkFLowModal ref={recordDetailModalRef} />
      <ApprovalModal
        ref={approvalModalRef}
        url={getIntranetDocUrl(approvalDocId, true)}
        docId={approvalDocId}
        approvalType={approvalType}
        dataItemChange={dataItemChange}
        handleClose={() => {
          clearNextDoc(); // 결재 응답 대기 중 닫으면 뒤늦게 레이어가 뜸
          closeApprovalModal();
        }}
        onDecided={handleDecided}
        onOpenNextDoc={openDoc}
      />
      <NextApprovalConfirmModal
        open={!!nextDoc}
        onOpenNext={() => {
          if (!nextDoc) return;
          openDoc(nextDoc);
        }}
        onClose={() => {
          clearNextDoc();
          closeApprovalModal();
        }}
      />
    </>
  );
};

export default NewNotificationsPopup;
